import { useState, useEffect, useMemo } from 'react';
import { useForm, useWatch, Controller } from 'react-hook-form';
import {
  Button,
  TextField,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { useNotifications } from '@/hooks/use-notifications';
import { useTranslations } from '@/i18n/use-translations.tsx';
import type { TranslationKey } from '@/i18n/translation-key.tsx';
import OtpCode from '@/components/invitation-flow/otp-code';
import { usePairDeviceMutation } from '@/state/mutations/player/use-pair-device';
import { PLAYER_TYPES } from '@/types/dtos/player/response/player.dto';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import { resolveConfigurationPlayerName } from '@/helpers/configuration-player-name';
import type { PlayerParentEntity } from '@/types/dtos/player/player-parent-entity';

type PairDeviceFormData = {
  deviceName: string;
  otpCode: string;
  deviceType: (typeof PLAYER_TYPES)[number];
};

interface PlayFormFieldsProps {
  entityId: string;
  entityType: PlayerParentEntity;
  eventSlotId?: string;
  onSuccess: () => void;
  onCancel?: () => void;
  showCancel?: boolean;
  /** When set, locks the device type and hides the type selector. */
  fixedType?: (typeof PLAYER_TYPES)[number];
  /** When true, pairing is shown but submit is disabled (stub sections). */
  stubMode?: boolean;
  stubMessage?: string;
  /**
   * Auto-assign `{type label} {n}` and hide the device name field.
   * Used by flow configuration pairing.
   */
  autoName?: boolean;
  /** Existing names for the locked/selected type (for auto-numbering). */
  existingPlayerNames?: Array<string | undefined | null>;
}

export const PlayFormFields = ({
  entityId,
  entityType,
  eventSlotId,
  onSuccess,
  onCancel,
  showCancel = true,
  fixedType,
  stubMode = false,
  stubMessage,
  autoName = false,
  existingPlayerNames = [],
}: PlayFormFieldsProps) => {
  const t = useTranslations();
  const notification = useNotifications();
  const [hasStartedTyping, setHasStartedTyping] = useState(false);
  /** Remount OTP inputs so digits never linger after cancel / success / failed pair. */
  const [otpSessionKey, setOtpSessionKey] = useState(0);
  const { mutateAsync: pairDeviceMutation, isPending: isPairing } = usePairDeviceMutation();
  const { hasAccess: hasPerformFeature, isLoading: isLoadingPerform } = useFeatureAccess('perform');
  const { hasAccess: hasBroadcastFeature, isLoading: isLoadingBroadcast } = useFeatureAccess('broadcast');

  const isLoadingFeatures = isLoadingPerform || isLoadingBroadcast;
  const isPending = isPairing;

  const availablePlayerTypes = useMemo(() => {
    if (isLoadingFeatures) return [];
    const types: string[] = [];
    if (hasBroadcastFeature) {
      types.push('onlinePlayer');
    }
    if (hasPerformFeature) {
      types.push('screenPublisher', 'roomPlayer', 'stagePlayer');
    }
    return types;
  }, [hasBroadcastFeature, hasPerformFeature, isLoadingFeatures]);

  const defaultPlayerType = useMemo(() => {
    if (availablePlayerTypes.length === 0) return 'onlinePlayer' as const;
    if (availablePlayerTypes.includes('onlinePlayer')) return 'onlinePlayer' as const;
    if (availablePlayerTypes.includes('screenPublisher')) return 'screenPublisher' as const;
    if (availablePlayerTypes.includes('roomPlayer')) return 'roomPlayer' as const;
    if (availablePlayerTypes.includes('stagePlayer')) return 'stagePlayer' as const;
    return availablePlayerTypes[0] as (typeof PLAYER_TYPES)[number];
  }, [availablePlayerTypes]);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    setError,
    formState: { errors },
  } = useForm<PairDeviceFormData>({
    mode: 'onChange',
    defaultValues: {
      deviceName: '',
      otpCode: '',
      deviceType: fixedType ?? 'onlinePlayer',
    },
  });

  const watchedDeviceType = useWatch({ control, name: 'deviceType', defaultValue: fixedType ?? 'onlinePlayer' });

  useEffect(() => {
    if (fixedType) {
      setValue('deviceType', fixedType);
      return;
    }
    if (isLoadingFeatures || availablePlayerTypes.length === 0) return;
    if (!availablePlayerTypes.includes(watchedDeviceType)) {
      setValue('deviceType', defaultPlayerType);
    }
  }, [fixedType, isLoadingFeatures, availablePlayerTypes, defaultPlayerType, setValue, watchedDeviceType]);

  const watchedOtpCode = useWatch({ control, name: 'otpCode', defaultValue: '' });
  const watchedDeviceName = useWatch({ control, name: 'deviceName', defaultValue: '' });

  const trimmedName = watchedDeviceName.trim();
  const canSubmitPairDevice = autoName
    ? /^\d{6}$/.test(watchedOtpCode)
    : Boolean(trimmedName) && trimmedName.length <= 100 && /^\d{6}$/.test(watchedOtpCode);

  const resolveAutoName = (deviceType: (typeof PLAYER_TYPES)[number]) =>
    resolveConfigurationPlayerName(deviceType, existingPlayerNames);

  const handleOtpChange = (code: string) => {
    setValue('otpCode', code);
    if (code && !hasStartedTyping) {
      setHasStartedTyping(true);
    }
  };

  const handleOtpConfirm = () => {
    if (canSubmitPairDevice) {
      handleSubmit(onPairSubmit)();
    }
  };

  const clearOtpFields = (deviceType?: (typeof PLAYER_TYPES)[number]) => {
    reset({
      deviceName: '',
      otpCode: '',
      deviceType: deviceType ?? fixedType ?? watchedDeviceType,
    });
    setHasStartedTyping(false);
    setOtpSessionKey((key) => key + 1);
  };

  const onPairSubmit = async (data: PairDeviceFormData) => {
    if (stubMode) {
      return;
    }
    const deviceType = fixedType ?? data.deviceType;
    const name = autoName ? resolveAutoName(deviceType) : data.deviceName.trim();
    if (!autoName) {
      if (!name) {
        setError('deviceName', {
          type: 'manual',
          message: t('flows.playModal.validation.deviceNameRequired' as TranslationKey),
        });
        return;
      }
      if (name.length > 100) {
        setError('deviceName', {
          type: 'manual',
          message: t('flows.playModal.validation.deviceNameTooLong' as TranslationKey),
        });
        return;
      }
    }
    if (!/^\d{6}$/.test(data.otpCode)) {
      setError('otpCode', {
        type: 'manual',
        message: t('flows.playModal.validation.otpCodeInvalid' as TranslationKey),
      });
      return;
    }
    try {
      await pairDeviceMutation({
        name,
        code: data.otpCode,
        entityId: entityId,
        entityType: entityType,
        type: deviceType,
        ...(eventSlotId ? { eventSlotId } : {}),
      });

      notification.notifySuccess(t('flow.notification.pairSuccess'));

      clearOtpFields(deviceType);
      onSuccess();
    } catch (error) {
      console.error('Pair device error:', error);
      notification.notifyError(t('flow.notification.pairError'));
      // Pairing codes are single-use; always start empty after a failed attempt.
      clearOtpFields(deviceType);
    }
  };

  const handleCancelClick = () => {
    clearOtpFields();
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <Stack spacing={1}>
      {stubMode && stubMessage ? (
        <Typography variant="body2" color="text.secondary">
          {stubMessage}
        </Typography>
      ) : null}

      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        useFlexGap
        flexWrap="nowrap"
        sx={{ width: '100%', minWidth: 0 }}
      >
        {!autoName ? (
          <Controller
            name="deviceName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t('flows.playModal.deviceNameLabel' as TranslationKey)}
                placeholder={t('flows.playModal.deviceNamePlaceholder' as TranslationKey)}
                size="small"
                variant="outlined"
                autoFocus
                error={!!errors.deviceName}
                helperText={errors.deviceName?.message}
                disabled={stubMode}
                sx={{ flex: '1 1 100px', minWidth: 80 }}
              />
            )}
          />
        ) : null}

        {!fixedType && !stubMode ? (
          <Controller
            name="deviceType"
            control={control}
            render={({ field }) => {
              const currentValue = field.value;
              const isValidValue = availablePlayerTypes.length > 0 && availablePlayerTypes.includes(currentValue);
              const displayValue = isValidValue
                ? currentValue
                : availablePlayerTypes.length > 0
                  ? defaultPlayerType
                  : '';

              return (
                <FormControl variant="outlined" error={!!errors.deviceType} sx={{ minWidth: 120 }} size="small">
                  <InputLabel id="device-type-label-fields">
                    {t('flows.playModal.deviceTypeLabel' as TranslationKey)}
                  </InputLabel>
                  <Select
                    value={displayValue}
                    onChange={(e) => {
                      const newValue = e.target.value as string;
                      if (availablePlayerTypes.includes(newValue)) {
                        field.onChange(newValue);
                      }
                    }}
                    onBlur={field.onBlur}
                    name={field.name}
                    labelId="device-type-label-fields"
                    id="device-type-select-fields"
                    label={t('flows.playModal.deviceTypeLabel' as TranslationKey)}
                    disabled={isLoadingFeatures || availablePlayerTypes.length === 0}
                  >
                    <MenuItem value="onlinePlayer" disabled={!hasBroadcastFeature}>
                      {t('flows.playModal.deviceTypeLivePlayer')}
                    </MenuItem>
                    <MenuItem value="stagePlayer" disabled={!hasPerformFeature}>
                      {t('flows.playModal.deviceTypeStagePlayer')}
                    </MenuItem>
                    <MenuItem value="screenPublisher" disabled={!hasPerformFeature}>
                      {t('flows.playModal.deviceTypeMediaPlayer')}
                    </MenuItem>
                    <MenuItem value="roomPlayer" disabled={!hasPerformFeature}>
                      {t('flows.playModal.deviceTypeRoomPlayer')}
                    </MenuItem>
                  </Select>
                  {errors.deviceType && <FormHelperText>{errors.deviceType.message}</FormHelperText>}
                </FormControl>
              );
            }}
          />
        ) : null}

        <OtpCode
          key={otpSessionKey}
          compact
          onChange={handleOtpChange}
          onConfirm={handleOtpConfirm}
          hasError={!!errors.otpCode}
        />

        <Stack direction="row" spacing={0.5} sx={{ ml: 'auto', flexShrink: 0 }}>
          {showCancel && (
            <Button color="primary" size="small" onClick={handleCancelClick} sx={{ minWidth: 0, px: 1 }}>
              {t('common.cancel')}
            </Button>
          )}
          <Button
            size="small"
            onClick={() => void handleSubmit(onPairSubmit)()}
            loading={isPending}
            variant="contained"
            disabled={stubMode || !canSubmitPairDevice || isPending}
            sx={{ minWidth: 0, px: 1.25 }}
          >
            {t('common.confirm')}
          </Button>
        </Stack>
      </Stack>

      {errors.otpCode ? (
        <Typography variant="body2" color="error">
          {errors.otpCode.message}
        </Typography>
      ) : null}
    </Stack>
  );
};
